// Shared domain types. Data now lives in Supabase (see lib/store.tsx); this file
// keeps the type definitions that the whole app imports.
import type { SyncFlags } from './sync-flags'
import type { Fit } from './fitment'

export type Category = {
  id: string
  name: string
  parent_id: string | null
  icon: string | null
  created_at: string
  /** 1.66 (192) — where this category's dishes are sent (a sub-category without one takes its parent's) */
  kitchen_station?: 'kitchen' | 'bar' | null
}

// Unit of measure: pieces by default; kg/m/l enable fractional quantities.
export type ProductUnit = 'piece' | 'kg' | 'm' | 'l'

export const UNIT_LABELS: Record<ProductUnit, string> = {
  piece: 'ცალი',
  kg: 'კგ',
  m: 'მ',
  l: 'ლ',
}

/** Short label shown next to quantities ("ც", "კგ"…). */
export const UNIT_SHORT: Record<ProductUnit, string> = {
  piece: 'ც',
  kg: 'კგ',
  m: 'მ',
  l: 'ლ',
}

export type Product = {
  id: string
  name: string
  barcode: string | null
  /** 158 — up to ten more codes that only FIND the product (the primary is
   *  `barcode`). Optional on purpose: a copy saved by 1.39 has none; read it
   *  through lib/barcodes.ts (extrasOf / productCodes), never directly. */
  extra_barcodes?: string[]
  /** 180 (1.58): how many pieces a box holds, and the box's own code (the till adds the whole box). */
  pack_qty?: number | null
  pack_barcode?: string | null
  category_id: string
  purchase_price: number
  sale_price: number
  wholesale_price: number | null   // optional second sell price (null = retail only)
  /** 188 (1.63): a club member's price; a copy cached before 1.63 has none (refetched, lib/store.tsx). */
  club_price?: number | null
  quantity: number
  /** Org-wide stock across every branch. Equals `quantity` in a single-branch
   *  shop; with several branches `quantity` is lensed to the ACTIVE branch
   *  (see the multi-branch sum rule) and this keeps the total the database
   *  actually holds. Ask for this one on any "all branches" figure. */
  org_quantity?: number
  unit: ProductUnit
  photo_url: string | null
  plu_code: string | null          // scale PLU (migration 036); null = not a scale item
  /** 1.61 (184): the earliest open batch's date, kept by the database — a
   *  cache for 1.60 tills and the Excel export. New code reads `stockLots`
   *  (lib/expiry.ts). On a new product it is the first batch's date. */
  expiry_date: string | null
  /** 1.61 (184) — „ვადიანი პროდუქტი": its stock comes in dated batches.
   *  Optional: rows from 1.60 have none. */
  track_expiry?: boolean
  /** 114 — the server's stamp of the last change; where a device's next
   *  „what changed since" read starts. */
  updated_at?: string
  /** 112 — sizes and colours. A model is a group of ordinary products tied by
   *  `model_id`; `model_name` is what the till's one tile says, `size` and
   *  `color` which one this is (see lib/variants.ts). All null for a product
   *  that is not part of a model. */
  model_id?: string | null
  model_name?: string | null
  size?: string | null
  color?: string | null
  /** 128 — the till asks for the IMEI / serial number of every unit sold. */
  track_serial?: boolean
  /** 128 — months of warranty (0–120); the server stamps the end date on the
   *  sale line. Null or 0 = none. Any product may carry one, serial or not. */
  warranty_months?: number | null
  /** 169 — a service: the till sells it, it is never stock (the database
   *  refuses any quantity on it). Read it through lib/service.ts. */
  is_service?: boolean
  /** 196 (1.68) — the shop's own table-service product (`table_service_set` makes it). Only a marked row carries the
   *  field; the store keeps marked rows out of `products` (serviceRows, serviceProductId). */
  system_kind?: 'table_service'
  /** 187 (1.62) — what the part fits (make · model · years · note) and its OEM / cross numbers; read them through
   *  lib/fitment.ts (fitsOf / oemOf). Optional: a copy saved by 1.61 has neither. Written only through
   *  `editProductFitment` once the product exists (the add/remove door), or in a new product's insert. */
  fits?: Fit[]
  oem_codes?: string[]
  /** 149 / 153 — the shop's website: `online` = switched on (a shop that lists
   *  by hand), `online_hidden` = kept off (a shop that lists automatically).
   *  The rule is the database's store_listed; lib/online-listing.ts says why. */
  online?: boolean
  online_hidden?: boolean
  created_at: string
}

export type FiscalStatus = 'none' | 'pending' | 'success' | 'failed'

export type SaleType = 'sale' | 'return'

/**
 * How a sale was settled. `mixed` (103) means it took BOTH rails; the amounts
 * live in `paid_cash` / `paid_card` and this word is only their label.
 *
 * The label is DERIVED by `create_sale`, never sent by the till: a client that
 * claimed „cash" while handing over a card amount would file a lie that every
 * later report repeats.
 */
/** 'prepaid' (175): a sale paid wholly from a gift card or a deposit; beside cash or card it is 'mixed'. */
export type PaymentMethod = 'cash' | 'card' | 'credit' | 'mixed' | 'prepaid'

/** Which acquirer a non-cash payment landed with — for statement reconciliation.
 *  `keepz` is a QR paid through Keepz (082): no terminal, but the money reaches
 *  the shop's account through an acquirer exactly as a card does, so it is a
 *  card payment to every report with its own column. */
export type CardProvider = 'tbc' | 'bog' | 'liberty' | 'keepz'

export const CARD_PROVIDER_LABELS: Record<CardProvider, string> = {
  tbc: 'TBC',
  bog: 'BOG',
  liberty: 'Liberty',
  keepz: 'QR (Keepz)',
}

/** 120 — the banks cash can be CARRIED to (a deposit, not a terminal): the
 *  same three a card sale names, never Keepz. Labels: CARD_PROVIDER_LABELS. */
export type BankCode = 'tbc' | 'bog' | 'liberty'
export const BANK_CODES: BankCode[] = ['tbc', 'bog', 'liberty']

export type Sale = {
  id: string
  total: number               // net total (after discount AND bonus)
  discount?: number           // discount amount applied
  /** 085: bonus spent on this sale (a return stores the part given back as
   *  a NEGATIVE, so a SUM over sales + returns is the net). */
  bonus_redeemed?: number
  /** 085: bonus this sale earned (a return: what it took back, negative). */
  bonus_earned?: number
  /** 092: the note the customer handed over on a cash sale (null when the
   *  cashier typed nothing, or on any other kind of sale). Change = this − total. */
  cash_received?: number | null
  /** 168 — the change rounding, signed ₾ (+ the shop kept, − it gave or forgave), |x| ≤ 0.09. Not part of the total. */
  change_kept?: number
  payment_method: PaymentMethod
  /**
   * 103 — what each rail actually took. `total - paid_cash - paid_card` is the
   * debt, which is why a mixed sale and a ნისია turned out to be the same shape.
   *
   * ⚠️ THE DRAWER READS `paid_cash`, never the method. Before 103 the deposit
   * on a ნისია had no method at all and three places ASSUMED it was cash; that
   * assumption is gone, not fixed. On a RETURN these hold what was handed BACK,
   * so a card refund never touches the drawer.
   */
  paid_cash: number
  paid_card: number
  /** 175 — the share a gift card or a deposit paid (a return: what went back to it). Part of
   *  `paid`, so `total − paid` is still the debt; the rails add up: cash + card + prepaid. */
  paid_prepaid?: number
  /** 175 — create_sale's reply for the receipt: the card's balance after this sale. Client only,
   *  never a column; absent on a reload or a history row. */
  prepaid_after?: { balance: number; code_tail: string; expires_at: string | null } | null
  customer_id?: string | null  // credit sales — and, since 085, any sale with a loyalty customer
  customer_name?: string | null
  paid?: number                // amount paid at sale time (credit down-payment)
  shift_id?: string | null
  cashier_id?: string | null
  cashier_name?: string | null
  items_count: number
  created_at: string
  type: SaleType
  reversal_of?: string | null // for returns: the original sale id
  card_provider?: CardProvider | null // which bank's terminal, when payment_method === 'card'
  branch_id?: string | null   // which branch sold it (default branch when single-branch)
  // Fiscalization
  is_fiscal: boolean
  fiscal_status: FiscalStatus
  fiscal_id: string | null
  fiscal_data?: Record<string, unknown> | null
  fiscalized_at?: string | null
  /** 140 — when the server received it, for a sale that came through the offline queue (created_at = when it happened). */
  received_at?: string | null
  /** 140 — what was unusual about it (lib/sync-flags.ts); null for an ordinary sale. */
  sync_flags?: SyncFlags | null
  /** 157 — both legs of an exchange carry the same id (the return of the old item, the sale of the new one). */
  exchange_id?: string | null
  /** 157 — on the sale leg: the FIRST purchase's date, the base of the exchange window. */
  exchange_bought_at?: string | null
}

export type SaleItem = {
  id: string
  sale_id: string
  product_id: string
  product_name: string
  barcode: string | null
  quantity: number
  unit_price: number
  total_price: number
  unit_cost: number   // product cost at sale time (for real margin); server-computed
  /** 086: the promotion that priced this line (NULL = list price) and the ₾
   *  it took off the WHOLE line. `total_price` is already net of it, so
   *  unit_price × quantity − promo_discount = total_price. */
  promotion_id?: string | null
  promo_discount?: number
  /** 128 — the units this line sold (or a return took back), one number per
   *  piece, in lib/serials.ts's normal form. Null on a line that names none. */
  serials?: string[] | null
  /** 128 — the warranty's last day (YYYY-MM-DD), set by the server at sale. */
  warranty_until?: string | null
  /** 182 (1.59) — set only on a ₾0 line the server wrote for an ingredient
   *  („ფასში შედის"): the id of the drink's line it rode with. */
  ingredient_of?: string | null
}

/** Signed money value of a sale row: returns count as negative against revenue. */
export function saleAmount(s: Pick<Sale, 'type' | 'total'>): number {
  return s.type === 'return' ? -s.total : s.total
}

// ---- Suppliers & purchasing ----
export type Supplier = {
  id: string
  name: string
  tin: string | null       // საიდენტიფიკაციო კოდი — the accountant needs it on purchase reports
  phone: string | null
  contact: string | null
  address: string | null
  note: string | null
  balance: number          // amount WE OWE the supplier (positive = our debt)
  created_at: string
}

export type Purchase = {
  id: string
  supplier_id: string | null
  supplier_name: string | null
  total: number
  paid: number
  items_count: number
  note: string | null
  rsge_waybill_id?: string | null  // set when the purchase came from an RS.GE waybill
  /** 171 — where the money paid at receiving came from; a drawer payment is a movement. */
  paid_source?: 'drawer' | 'bank' | 'owner' | 'other' | null   // 'owner' = 183
  cash_movement_id?: string | null
  branch_id?: string | null        // which branch received the goods
  created_at: string
}

/** 170 — goods that went back to a supplier: the debt way lowers the balance,
 *  the cash way is a drawer movement in. Written only by return_to_supplier. */
export type SupplierReturn = {
  id: string
  supplier_id: string
  branch_id: string | null
  shift_id: string | null
  purchase_id: string | null
  total: number
  settle: 'debt' | 'cash'
  cash_movement_id: string | null
  /** Signed: + a loss booked as a write-off, − a gain shown, not booked. */
  cost_diff: number
  note: string | null
  created_by: string | null
  created_at: string
}

export type SupplierReturnItem = {
  id: string
  return_id: string
  product_id: string | null
  purchase_item_id: string | null
  product_name: string
  barcode: string | null
  quantity: number
  unit_credit: number
  total_credit: number
  unit_cost: number
  cost_diff: number
}

export type PurchaseItem = {
  id: string
  purchase_id: string
  product_id: string | null
  product_name: string
  barcode: string | null
  quantity: number
  unit_cost: number
  total_cost: number
}

export type SupplierPayment = {
  id: string
  supplier_id: string
  amount: number
  note: string | null
  method: PayMethod
  card_provider?: CardProvider | null
  shift_id: string | null
  created_at: string
  /** Which branch this money moved through. NULL = older than migration 060,
   *  or booked while the org had several branches and nothing said which. It is
   *  left NULL rather than guessed: see the backfill note in 060. */
  branch_id?: string | null
}

// ---- Customers & credit (ნისია) ----
export type Customer = {
  id: string
  name: string
  phone: string | null
  note: string | null
  balance: number          // amount the customer OWES US (positive = their debt)
  /** 085: loyalty bonus in ₾ — earned on sales, spent on sales. Can go below
   *  zero when a return takes back bonus that was already spent. */
  bonus_balance: number
  /** 096: a company buyer — printed on its invoice. Not money; the cashier may fill them. */
  tin?: string | null
  legal_name?: string | null
  legal_address?: string | null
  /** 178 (1.58): the price group that prices this customer at the till — set by the owner only. */
  price_group_id?: string | null
  /** 188 (1.63) — the club identity; written only by the club RPCs (lib/club.ts CLUB_CUSTOMER_COLUMNS).
   *  card_token is the bearer card: the owner's screens send its link, the till never shows it. */
  email?: string | null
  email_verified_at?: string | null
  card_token?: string | null
  phone_norm?: string | null
  phone_confirmed_at?: string | null
  joined_via?: 'self' | 'cashier' | 'owner' | null
  anonymized_at?: string | null
  created_at: string
}

/** 178 (1.58) — a RULE, not a price list: the product's wholesale price, or N % off retail. Never deleted, switched off. */
export type PriceGroup = {
  id: string
  name: string
  rule: 'wholesale' | 'pct'
  pct: number | null
  active: boolean
  created_at: string
}

export type PayMethod = 'cash' | 'card' | 'transfer'

export type CustomerPayment = {
  id: string
  customer_id: string
  amount: number
  note: string | null
  method: PayMethod
  card_provider?: CardProvider | null
  shift_id: string | null
  created_at: string
  /** Which branch this money moved through. NULL = older than migration 060,
   *  or booked while the org had several branches and nothing said which. It is
   *  left NULL rather than guessed: see the backfill note in 060. */
  branch_id?: string | null
}

// ---- Operational expenses (ხარჯები) ----
export type ExpenseCategory = 'rent' | 'salary' | 'utilities' | 'transport' | 'marketing' | 'bank' | 'taxes' | 'writeoff' | 'other'

export const EXPENSE_CATEGORY_LABELS: Record<ExpenseCategory, string> = {
  rent: 'ქირა',
  salary: 'ხელფასი',
  utilities: 'კომუნალური',
  transport: 'ტრანსპორტი',
  marketing: 'რეკლამა',
  bank: 'საბანკო მომსახურება',
  taxes: 'გადასახადები',
  // Written by `write_off_stock` (076) and never by hand: a write-off is
  // units leaving the shelf AND their cost becoming an expense, in one
  // transaction. Typing it as a plain expense would book the loss twice
  // over — once here and once when the stock was corrected separately.
  writeoff: 'ჩამოწერილი საქონელი',
  other: 'სხვა',
}

/** The categories a person may choose when adding an expense by hand. */
export const MANUAL_EXPENSE_CATEGORIES = (Object.keys(EXPENSE_CATEGORY_LABELS) as ExpenseCategory[])
  .filter((c) => c !== 'writeoff')

// ---- Tax status (საგადასახადო სტატუსი) — drives the tax card in reports ----
// 'small' = მცირე ბიზნესის სტატუსი (1% of turnover, declared monthly)
// 'vat'   = დღგ-ს გადამხდელი (18%, prices are VAT-inclusive in Georgia)
export type TaxStatus = 'none' | 'small' | 'vat'

export const TAX_STATUS_LABELS: Record<TaxStatus, string> = {
  none: 'არ არის მითითებული',
  small: 'მცირე ბიზნესი (1%)',
  vat: 'დღგ-ს გადამხდელი (18%)',
}

export type Expense = {
  id: string
  category: ExpenseCategory
  amount: number
  note: string | null
  vendor_tin: string | null // counterparty's ს/კ — lets a VAT-registered org claim input VAT
  expense_date: string      // 'YYYY-MM-DD' — the day the money was spent
  created_at: string
  /** Which branch this money moved through. NULL = older than migration 060,
   *  or booked while the org had several branches and nothing said which. It is
   *  left NULL rather than guessed: see the backfill note in 060. */
  branch_id?: string | null
  /** Set only by a write-off (077): which product was lost, and how many.
   *  The note still spells it out for the audit trail — these two are what
   *  the screen reads, so a photo can be shown without parsing a sentence. */
  product_id?: string | null
  quantity?: number | null
  /** 107 — set when the money came out of the drawer during a shift. Deleting
   *  that movement (while the shift is open) deletes this row with it;
   *  deleting this row leaves the movement, because the cash did leave. */
  cash_movement_id?: string | null
  /** 121 — the monthly cost that booked this row. Deleting the row does not
   *  bring it back next month's run; deleting the cost keeps the row. */
  recurring_id?: string | null
  /** 137 — a salary booked by the close of this shift. */
  shift_id?: string | null
}

// ---- Loss control (დანაკარგების კონტროლი, 122) ----
/** How a line left a cart without being sold: removed, fewer of it, the
 *  whole cart cleared, a held cart discarded, a cart replaced by a held one. */
/** 1.65 (191) — what a buyer asked for and the shop did not have. The till writes it
 *  (through `lib/demand-queue.ts`); only the owner reads the list and closes it. */
export type DemandRecord = {
  id: string
  branch_id: string | null
  shift_id: string | null
  /** null = the buyer's own words: a product the shop has never stocked */
  product_id: string | null
  name: string
  qty: number
  /** who to call back — a name and/or a phone, free text */
  contact: string | null
  note: string | null
  created_by: string | null
  created_at: string
  status: 'open' | 'closed'
  closed_at: string | null
  /** the supplier order it went into (191: claimed under a lock) */
  order_id: string | null
}

/** 1.65 (191) — an order to one supplier (or none: a shopping list), for one branch.
 *  `received` only ever comes from the purchase that took it in (`sorder:` ref). */
export type SupplierOrderStatus = 'open' | 'received' | 'closed' | 'cancelled'
export type SupplierOrder = {
  id: string
  branch_id: string
  supplier_id: string | null
  supplier_name: string | null
  number: string
  status: SupplierOrderStatus
  note: string | null
  created_at: string
  sent_at: string | null
  closed_at: string | null
  purchase_id: string | null
}
export type SupplierOrderItem = {
  id: string
  order_id: string
  /** null = a free-text line from a need: ordered, never received as stock */
  product_id: string | null
  name: string
  unit: string | null
  qty: number
  /** the estimate the owner sees (the last cost); never sent to the supplier */
  unit_cost: number | null
  position: number
}

export type RemovalKind = 'line' | 'qty' | 'clear' | 'held' | 'replaced'

export type CartRemoval = {
  id: string
  /** One action — a cleared cart's lines share it. */
  group_id: string
  kind: RemovalKind
  product_id: string | null
  product_name: string
  quantity: number
  unit_price: number
  /** Computed by the database: quantity × unit_price. */
  amount: number
  /** The cart's total just before, at retail prices. */
  cart_total: number | null
  /** The payment window had been opened for that cart. */
  after_payment: boolean
  shift_id: string | null
  cashier_id: string | null
  /** From the shift, by the database — never the device's word. */
  cashier_name: string | null
  branch_id: string | null
  happened_at: string
  created_at: string
}

export type PriceChange = {
  id: string
  product_id: string | null
  product_name: string
  old_sale_price: number | null
  new_sale_price: number | null
  old_wholesale_price: number | null
  new_wholesale_price: number | null
  changed_by: string | null
  /** owner / cashier / accountant / support — null for a service job. */
  changed_by_role: string | null
  /** The device's open shift when the till said which (x-shift-id). */
  shift_id: string | null
  cashier_name: string | null
  /** One save — a model's sizes — shares it. */
  txid: number
  created_at: string
}

// ---- Monthly costs (ყოველთვიური ხარჯები, 121) ----
/** auto — the amount is the same every month and books itself on the day;
 *  ask — it changes (utilities): on the day the owner is asked for it. */
export type RecurringMode = 'auto' | 'ask'

export type RecurringExpense = {
  id: string
  category: ExpenseCategory
  /** auto: what books. ask: last month's amount, offered again. */
  amount: number
  note: string | null
  vendor_tin: string | null
  branch_id: string | null
  /** 1–31; 29–31 fall on a shorter month's last day. */
  day_of_month: number
  mode: RecurringMode
  active: boolean
  /** 'YYYY-MM-DD' — the next occurrence not yet booked (Tbilisi calendar). */
  next_due: string
  created_at: string
}

// ---- Cashiers & shifts (ცვლები) ----
/** 116/117 — every column of `cashiers` but the PIN. 117 takes SELECT on
 *  `pin` away from the app, so `select('*')` on this table is an error; a new
 *  column needs a GRANT (117) and a place here. */
export const CASHIER_COLUMNS = 'id,name,active,created_at,org_id,user_id,branch_id,max_discount_pct,kind'

export type Cashier = {
  id: string
  name: string
  /** 116: NOT loaded any more — a device that holds every cashier's PIN lets
   *  one cashier open a shift in another's name. Set only when WRITING a PIN;
   *  the owner reads them through `cashierPins()`. */
  pin?: string
  active: boolean
  branch_id?: string | null  // 066: NULL = floats, works at every branch
  /** 084: the most this cashier may take off a cart, in % of the lines'
   *  sum; above it the owner's PIN is needed. NULL = no ceiling. */
  max_discount_pct?: number | null
  created_at: string
  /** 058: the login this row belongs to (a per-person cashier login; NULL for a PIN-only row) */
  user_id?: string | null
  /** 1.67 (193): a waiter is a cashier-class login linked to a row of this kind */
  kind?: 'cashier' | 'waiter'
}

export type Shift = {
  id: string
  cashier_id: string | null
  cashier_name: string | null
  status: 'open' | 'closed'
  opening_cash: number
  closing_cash: number | null
  cash_sales: number
  card_sales: number
  credit_sales: number
  credit_paid: number
  returns_total: number
  sales_count: number
  customer_payments_cash: number
  supplier_payments_cash: number
  expected_cash: number
  difference: number
  /** 107 — money put into / taken out of the drawer during the shift, with a
   *  reason. Frozen at close like every other Z-report column; 0 while open. */
  cash_in_total: number
  cash_out_total: number
  /** 168 — the change rounding at close: tetri kept / tetri given (both ≥ 0) */
  change_kept_in?: number
  change_kept_out?: number
  /** 176 — money held for customers, frozen at close: taken for a gift card or a deposit in
   *  cash / by card, handed back in cash / by card, and the sales a prepaid paid (a rail). */
  prepaid_in_cash?: number
  prepaid_in_card?: number
  prepaid_out_cash?: number
  prepaid_out_card?: number
  prepaid_sales?: number
  /** 119 — left in the drawer for the next shift at close (null before 119:
   *  the whole count stayed). The rest was taken, with a reason. */
  closing_float?: number | null
  takeout_reason?: 'owner' | 'bank' | 'other' | null
  takeout_note?: string | null
  /** 120 — which bank, when the take-out went „ბანკში" (null = not named). */
  takeout_bank?: BankCode | null
  /** 137 — the cashier's salary paid from the count at close (null = none). */
  closing_salary?: number | null
  /** 119 — what the previous shift left, set by the database at opening. */
  opening_carried?: number | null
  branch_id?: string | null  // shifts are per-branch since migration 041
  opened_at: string
  closed_at: string | null
}

// ---- The drawer (107): money in or out during a shift, with a reason ----
// Always inside an open shift. A withdrawal whose reason is `expense` also
// books a row in `expenses` (expenses.cash_movement_id), so profit sees it once.
export type CashDirection = 'in' | 'out'
export type CashMovementReason = 'owner' | 'expense' | 'bank' | 'change' | 'other'
/** 170 / 171 — the document that wrote a movement (a return, a receipt); its reason is
 *  then 'other', which a 1.53 till already knows, and its note names the supplier. */
export type CashDoc = 'supplier_return' | 'purchase'

// ---- Money a customer leaves with us (174): a gift card or a deposit ----
// Taking it is not a sale; spending it is (sales.paid_prepaid). The balance moves only
// through the server's functions; a cashier sees a gift card's code as its last four.
export type PrepaidKind = 'gift' | 'deposit'
export type PrepaidStatus = 'open' | 'spent' | 'refunded'
/** How money came in for a prepaid or went back: never a bonus, another prepaid or ნისია. */
export type PrepaidMethod = 'cash' | 'card' | 'transfer'

export type Prepaid = {
  id: string
  kind: PrepaidKind
  /** the whole code for the owner or whoever typed it; „••••4821" for a gift card in a cashier's list */
  code: string
  code_tail: string
  customer_id: string | null
  customer_name: string | null
  customer_phone: string | null
  note: string | null
  /** a deposit's lines from the cart (a layaway); [] for a gift card */
  items: HeldCartItem[]
  due_date: string | null
  expires_at: string | null
  amount: number
  balance: number
  status: PrepaidStatus
  branch_id: string | null
  shift_id: string | null
  cashier_name: string | null
  created_at: string
}

export type PrepaidMoveKind = 'issue' | 'redeem' | 'return' | 'refund'

export type PrepaidMove = {
  id: string
  prepaid_id: string
  kind: PrepaidMoveKind
  /** signed: + issue / return, − redeem / refund */
  amount: number
  method: PrepaidMethod | null
  card_provider: BankCode | null
  shift_id: string | null
  sale_id: string | null
  branch_id: string | null
  cashier_name: string | null
  note: string | null
  created_at: string
}

export type CashMovement = {
  id: string
  shift_id: string | null
  branch_id?: string | null
  direction: CashDirection
  amount: number
  reason: CashMovementReason
  note: string | null
  /** 120 — which bank a „ბანკში შეტანა" went to (null = not named). */
  bank?: BankCode | null
  /** 170 — written by a document, never picked by a person (null = by hand). */
  doc?: CashDoc | null
  /** The login that wrote it — the owner, or this person, may take it back
   *  while the shift is open. */
  created_by: string | null
  created_at: string
}

/** Which reasons each direction may carry — the same list as the CHECK in 107. */
export const CASH_REASONS: Record<CashDirection, CashMovementReason[]> = {
  out: ['owner', 'expense', 'bank', 'other'],
  in: ['change', 'other'],
}

export const CASH_REASON_LABELS: Record<CashMovementReason, string> = {
  owner: 'მფლობელმა წაიღო',
  expense: 'ხარჯი',
  bank: 'ბანკში შეტანა',
  change: 'ხურდის დამატება',
  other: 'სხვა',
}

/** A document's movement says what the document is, not „სხვა". */
export const CASH_DOC_LABELS: Record<CashDoc, string> = {
  supplier_return: 'მომწოდებლის დაბრუნება',
  purchase: 'მიღება სალაროდან',
}

/** The expense categories a drawer withdrawal may book — `record_cash_movement` (107) refuses any other. */
export const DRAWER_EXPENSE_CATEGORIES = ['salary', 'transport', 'utilities', 'other'] as const
export type DrawerExpenseCategory = typeof DRAWER_EXPENSE_CATEGORIES[number]

// ---- Branches (multi-branch, Design C) ----
// The org-wide catalog lives in products; per-branch quantities live in
// product_stock. Branch is a FILTER, not a security boundary (org RLS is).
export type Branch = {
  id: string
  name: string
  address: string | null
  is_default: boolean
  active: boolean
  created_at: string
}

// Stock transfer between two branches (multi-branch Stage 3). Org-wide
// stock-neutral: quantity leaves from_branch and arrives at to_branch, so
// products.quantity (the SUM) is unchanged.
export type StockTransfer = {
  id: string
  from_branch: string
  to_branch: string
  items_count: number
  note: string | null
  created_by: string | null
  created_at: string
}

export type StockTransferItem = {
  id: string
  transfer_id: string
  product_id: string | null
  product_name: string
  barcode: string | null
  quantity: number
  /** 131: cost per unit on the day it moved (private.product_cost); null = none known. */
  unit_cost: number | null
}

/**
 * A combo link: selling `product_id` prompts the cashier to also add
 * `addon_id` (coffee → cup, lid). The add-on is an ordinary stocked
 * product — it is sold and depleted like any other line, this is an
 * upsell prompt, not a recipe. Migration 044.
 */
export type ProductAddon = {
  id: string
  /** Scope: EXACTLY ONE of these is set (DB CHECK). */
  product_id: string | null
  /** Category rule (migration 045) — covers every product in the category,
   *  including ones added later. A per-product rule wins over it. */
  category_id: string | null
  addon_id: string
  sort_order: number
  /** TRUE = goes straight into the cart with the trigger, no prompt. */
  auto_add: boolean
}

/**
 * 182 (1.59) — „ფასში შედის": what ONE unit of a product takes off the shelf
 * when it is sold (a coffee: 1 cup, 1 lid, 7 g of a kg bag). The drink keeps
 * its own price; the server writes each ingredient as a ₾0 sale line with its
 * own cost. Scope exactly like `ProductAddon` — one product, or a category and
 * everything under it. The rule that reads these is `lib/ingredients.ts`.
 */
export type ProductIngredient = {
  id: string
  product_id: string | null
  category_id: string | null
  ingredient_id: string
  /** Per one unit of the product, in the INGREDIENT's own unit: 0.007 = 7 g of a kg product. */
  qty: number
  sort_order: number
}

/**
 * A supplier's own product code translated to one of our products
 * (migration 047). Distributor waybills carry an internal code, not the
 * EAN on the packet, so this frees `products.barcode` to hold the real
 * barcode while waybills still land on the right product.
 * Keyed by supplier TIN: two distributors can reuse the same code.
 */
export type SupplierProductCode = {
  id: string
  supplier_tin: string
  supplier_code: string
  product_id: string
  /** How many of OUR units one of the supplier's units holds (078). A
   *  waybill line „1 × ₾58" for a block of ten packs is received as 10 at
   *  ₾5.80 when this is 10. Property of the supplier's code, not of the
   *  product — the same packs may come loose from another supplier. 1 =
   *  the waybill's unit is our unit. */
  pack_size: number
}

/**
 * A product as it appears in the SHARED catalog (migration 048) — the
 * manufacturer's facts only. There is deliberately no price, cost, stock or
 * supplier here: those cannot leak between shops because they are not part
 * of the shape.
 */
export type CatalogProduct = {
  barcode: string
  name: string
  unit: ProductUnit
  photo_url: string | null
  /** Contributor's DIRECT category name (the leaf). Often a SKU rather than a
   *  real category — see lib/catalog-category.ts, which decides what to do. */
  category_hint: string | null
  /** Contributor's PARENT category name; null at top level (migration 056). */
  parent_hint: string | null
}

/** One "1 jar → N servings" stock conversion (migration 046). */
export type ProductConversion = {
  id: string
  branch_id: string
  source_product_id: string | null
  source_name: string
  source_qty: number
  target_product_id: string | null
  target_name: string
  target_qty: number
  total_cost: number
  unit_cost: number
  note: string | null
  created_by: string | null
  created_at: string
}

/** One part of a one → many breakdown (1.55, migration 172) — a snapshot of what it received. */
export type BreakdownPart = {
  id: string
  position: number
  product_id: string | null
  product_name: string
  unit: ProductUnit
  quantity: number
  sale_price: number
  sale_value: number
  cost: number
  unit_cost: number
  cost_before: number
  cost_after: number
}

/** A carcass into its cuts, a set into its pieces (1.55, migration 172). Immutable history. */
export type ProductBreakdown = {
  id: string
  branch_id: string
  source_product_id: string | null
  source_name: string
  source_unit: ProductUnit
  source_qty: number
  source_unit_cost: number
  total_cost: number
  basis: 'value' | 'quantity'
  /** Σ of the parts when every part is in the source's weighed unit — the yield; else null. */
  parts_qty: number | null
  note: string | null
  created_by: string | null
  created_at: string
  parts: BreakdownPart[]
}

export type DiscountType = 'amount' | 'percent'

/** A single line item inside a held (parked) cart. Mirrors the POS CartItem shape. */
export type HeldCartItem = {
  product_id: string
  product_name: string
  barcode: string | null
  unit_price: number
  wholesale_price?: number | null
  quantity: number
  unit?: ProductUnit
  photo_url: string | null
  /** 128 — the units' numbers, so a parked phone keeps its IMEI. */
  serials?: string[]
}

/** A parked cart saved to Supabase so it survives refresh / other devices. */
export type HeldCart = {
  id: string
  label: string | null
  items: HeldCartItem[]
  discount: number              // computed discount amount
  discount_type: DiscountType | null
  discount_value: number        // raw value the cashier typed (₾ or %)
  total: number                 // final total snapshot (after discount)
  items_count: number
  /** 178 (1.58): the cart comes back with its customer (and so with his group price). */
  customer_id?: string | null
  /** 178 (1.58): a kept quote's own terms — printed again from here. */
  quote?: QuoteSnapshot | null
  created_at: string
}

/** 1.58 — a quote before payment (ფასის შეთავაზება): its buyer, its rows (each adds up) and its terms. */
export type QuoteBuyer = { name: string; tin: string; address: string }
export type QuoteRow = { name: string; qty: number; unit: string; price: number; amount: number }
export type QuoteSnapshot = { number: string; buyer: QuoteBuyer; valid_until: string; note: string; rows: QuoteRow[]; total: number }
